import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import TinyMCEEditor from '../components/TinyMCEEditor';
import { 
  ArrowLeft,
  MessageCircle, 
  Clock, 
  Calendar, 
  User, 
  BarChart3, 
  Globe,
  Monitor,
  MapPin,
  Bot,
  UserIcon,
  Edit3,
  Save,
  X
} from 'lucide-react';

const SessionDetail = () => {
  const { sessionId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);
  const [editedResponse, setEditedResponse] = useState('');
  const [saving, setSaving] = useState(false);

  // Determine if this is a client or admin view
  const isClientView = window.location.pathname.includes('/client/');

  useEffect(() => {
    fetchSessionDetail();
  }, [sessionId]);

  const fetchSessionDetail = async () => {
    try {
      const response = await axios.get(`/chat-sessions/session/${sessionId}`);
      setSession(response.data.session);
    } catch (error) {
      console.error('Error fetching session detail:', error);
      setError('Failed to load session details');
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (milliseconds) => {
    if (!milliseconds || milliseconds < 0) return '0s';
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const formatResponseTime = (ms) => {
    return `${ms}ms`;
  };

  const getUserAgent = (userAgent) => {
    if (!userAgent) return 'Unknown Browser';
    if (userAgent.includes('Chrome')) return 'Google Chrome';
    if (userAgent.includes('Firefox')) return 'Mozilla Firefox';
    if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) return 'Safari';
    if (userAgent.includes('Edge')) return 'Microsoft Edge';
    return 'Unknown Browser';
  };

  const getOSFromUserAgent = (userAgent) => {
    if (!userAgent) return 'Unknown OS';
    if (userAgent.includes('Windows')) return 'Windows';
    if (userAgent.includes('Mac')) return 'macOS';
    if (userAgent.includes('Linux')) return 'Linux';
    if (userAgent.includes('Android')) return 'Android';
    if (userAgent.includes('iPhone') || userAgent.includes('iPad')) return 'iOS';
    return 'Unknown OS';
  };

  const handleEditResponse = (message) => {
    setEditingMessage(message);
    setEditedResponse(message.botResponse);
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editingMessage || !editedResponse.trim()) {
      return;
    }

    setSaving(true);
    try {
      await axios.post('/chat-sessions/improve-response', {
        sessionId: session.sessionId,
        messageId: editingMessage._id,
        userQuery: editingMessage.userQuery,
        originalResponse: editingMessage.botResponse,
        improvedResponse: editedResponse,
        contextUsed: editingMessage.contextUsed
      });

      // Update the message in the current session
      setSession(prevSession => ({
        ...prevSession,
        messages: prevSession.messages.map(msg => 
          msg._id === editingMessage._id 
            ? { ...msg, botResponse: editedResponse, isImproved: true }
            : msg
        )
      }));

      setShowEditModal(false);
      setEditingMessage(null);
      setEditedResponse('');
      
      alert('Response improved and saved! This will help improve future responses.');
    } catch (error) {
      console.error('Error saving improved response:', error);
      alert('Error saving improved response. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCloseModal = () => {
    setShowEditModal(false);
    setEditingMessage(null);
    setEditedResponse('');
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error || !session) {
    return (
      <div className="text-center py-12">
        <MessageCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500">{error || 'Session not found'}</p>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Go Back
        </button>
      </div>
    );
  }

  const backUrl = isClientView ? '/client/chat-sessions' : '/chat-sessions';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <Link
              to={backUrl}
              className="mr-4 p-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Chat Session Details</h1>
              <p className="text-gray-600">Session ID: {session.sessionId}</p>
            </div>
          </div>
        </div>

        {/* Session Info Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="flex items-center">
              <MessageCircle className="h-6 w-6 text-blue-500" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Messages</p>
                <p className="text-lg font-bold text-gray-900">{session.messageCount}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="flex items-center">
              <Clock className="h-6 w-6 text-green-500" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Duration</p>
                <p className="text-lg font-bold text-gray-900">{formatDuration(session.duration)}</p>
              </div>
            </div>
          </div>

          <div className="bg-purple-50 p-4 rounded-lg">
            <div className="flex items-center">
              <BarChart3 className="h-6 w-6 text-purple-500" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Avg Response</p>
                <p className="text-lg font-bold text-gray-900">{formatResponseTime(session.avgResponseTime)}</p>
              </div>
            </div>
          </div>

          <div className="bg-orange-50 p-4 rounded-lg">
            <div className="flex items-center">
              <Calendar className="h-6 w-6 text-orange-500" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Started</p>
                <p className="text-lg font-bold text-gray-900">
                  {new Date(session.startTime).toLocaleDateString()}
                </p>
                <p className="text-xs text-gray-500">
                  {new Date(session.startTime).toLocaleTimeString()}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* User Information */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-sm font-medium text-gray-700 mb-3">User Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="flex items-center">
              <MapPin className="h-4 w-4 text-gray-400 mr-2" />
              <span className="text-gray-600">IP Address:</span>
              <span className="ml-2 font-medium text-gray-900">{session.userIp || 'Unknown'}</span>
            </div>
            <div className="flex items-center">
              <Monitor className="h-4 w-4 text-gray-400 mr-2" />
              <span className="text-gray-600">Browser:</span>
              <span className="ml-2 font-medium text-gray-900">{getUserAgent(session.userAgent)}</span>
            </div>
            <div className="flex items-center">
              <Globe className="h-4 w-4 text-gray-400 mr-2" />
              <span className="text-gray-600">Platform:</span>
              <span className="ml-2 font-medium text-gray-900">{getOSFromUserAgent(session.userAgent)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Conversation */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Conversation History</h2>
          <p className="text-sm text-gray-600">
            {session.messageCount} messages • {formatDuration(session.duration)} total duration
          </p>
        </div>

        <div className="p-6">
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {session.messages.map((message, index) => (
              <div key={`${message._id}-${index}`} className="flex items-start space-x-3">
                {/* Avatar */}
                <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                  message.userQuery ? 'bg-blue-100' : 'bg-gray-100'
                }`}>
                  {message.userQuery ? (
                    <UserIcon className="h-4 w-4 text-blue-600" />
                  ) : (
                    <Bot className="h-4 w-4 text-gray-600" />
                  )}
                </div>

                <div className="flex-1">
                  {/* User Message */}
                  {message.userQuery && (
                    <div className="mb-2">
                      <div className="flex items-center mb-1">
                        <span className="text-sm font-medium text-gray-900">User</span>
                        <span className="text-xs text-gray-500 ml-2">
                          {new Date(message.createdAt).toLocaleTimeString()}
                        </span>
                      </div>
                      <div className="bg-blue-50 p-3 rounded-lg">
                        <p className="text-sm text-gray-900">{message.userQuery}</p>
                      </div>
                    </div>
                  )}

                  {/* Bot Response */}
                  <div>
                    <div className="flex items-center mb-1">
                      <span className="text-sm font-medium text-gray-900">Assistant</span>
                      <span className="text-xs text-gray-500 ml-2">
                        {new Date(message.createdAt).toLocaleTimeString()}
                      </span>
                      <span className="text-xs text-gray-400 ml-2">
                        ({formatResponseTime(message.responseTime)})
                      </span>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg relative group">
                      <div 
                        className="text-sm text-gray-900 pr-8"
                        dangerouslySetInnerHTML={{ __html: message.botResponse }}
                      />
                      {message.isImproved && (
                        <div className="inline-flex items-center mt-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                          ✓ Improved Response
                        </div>
                      )}
                      <button
                        onClick={() => handleEditResponse(message)}
                        className="absolute top-2 right-2 p-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white rounded shadow hover:bg-gray-100 z-10"
                        title="Improve this response"
                        style={{ transition: 'opacity 0.2s ease-in-out' }}
                      >
                        <Edit3 className="h-3 w-3 text-gray-600" />
                      </button>
                    </div>

                    {/* Context Used */}
                    {message.contextUsed && message.contextUsed.length > 0 && (
                      <div className="mt-2">
                        <details className="text-xs">
                          <summary className="text-gray-500 cursor-pointer hover:text-gray-700">
                            Used knowledge ({message.contextUsed.length} items)
                          </summary>
                          <div className="mt-1 pl-4 border-l-2 border-gray-200">
                            {message.contextUsed.map((ctx, ctxIndex) => (
                              <div key={ctxIndex} className="text-gray-600 mb-1">
                                • {ctx.contextId?.title || 'Unknown'} 
                                <span className="text-gray-400 ml-1">
                                  (relevance: {Math.round(ctx.relevanceScore * 100)}%)
                                </span>
                              </div>
                            ))}
                          </div>
                        </details>
                      </div>
                    )}

                    {/* Always visible edit button */}
                    <div className="mt-2 flex justify-end">
                      <button
                        onClick={() => handleEditResponse(message)}
                        className="text-xs text-blue-600 hover:text-blue-800 flex items-center transition-colors"
                        title="Improve this response"
                      >
                        <Edit3 className="h-3 w-3 mr-1" />
                        Improve Response
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Edit Response Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    Improve Bot Response
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Edit this response to improve future similar questions
                  </p>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
              {/* User Question */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  User Question:
                </label>
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-900">{editingMessage?.userQuery}</p>
                </div>
              </div>

              {/* Original Response */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Original Bot Response:
                </label>
                <div className="bg-gray-50 p-3 rounded-lg border">
                  <div 
                    className="text-sm text-gray-900"
                    dangerouslySetInnerHTML={{ __html: editingMessage?.botResponse }}
                  />
                </div>
              </div>

              {/* Improved Response */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Improved Response: *
                </label>
                <TinyMCEEditor
                  value={editedResponse}
                  onChange={setEditedResponse}
                  placeholder="Enter the improved response here. You can use formatting, links, lists, etc."
                  height={300}
                />
                <p className="text-xs text-gray-500 mt-1">
                  This improved response will be saved as high-priority knowledge and used for similar questions in the future.
                </p>
              </div>

              {/* Context Information */}
              {editingMessage?.contextUsed && editingMessage.contextUsed.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Knowledge Items Used:
                  </label>
                  <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                    {editingMessage.contextUsed.map((ctx, ctxIndex) => (
                      <div key={ctxIndex} className="text-sm text-gray-700 mb-1">
                        • {ctx.contextId?.title || 'Unknown'} 
                        <span className="text-gray-500 ml-1">
                          (relevance: {Math.round(ctx.relevanceScore * 100)}%)
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 bg-gray-50">
              <div className="flex justify-between items-center">
                <p className="text-sm text-gray-600">
                  <strong>Note:</strong> This will create a new high-priority knowledge item that will be used for similar questions.
                </p>
                <div className="flex space-x-3">
                  <button
                    onClick={handleCloseModal}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveEdit}
                    disabled={saving || !editedResponse.trim()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Save Improvement
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionDetail;